import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Select, { ValueType } from 'react-select';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { LeadUiModel } from '../../../shared/ui-models/lead.ui-model';
import { LeadConfigModel, LeadSearchQueryModel } from '../../../shared/models/lead.model';
import { createLead, getLeadConfig, searchLeads } from '../../../shared/services/data/lead.data';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { Pagination } from '../../../shared/components/core-ui/pagination/pagination.component';
import { StaffPicker } from '../../../shared/components/group-ui/staff-picker/staff-picker.component';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { ErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { SlaBadge } from '../../shared/sla-badge.component';
import {
  CHIP,
  CHIP_PRIMARY,
  CHIP_QUIET,
  CHIP_SECONDARY,
  PENDING_REASON_CHIP,
  STATUS_STYLE,
  VERTICAL_STYLE,
  resolutionStyle,
  styleFor
} from '../../shared/lead-visuals';
import { LeadSourceChip } from '../../shared/lead-source-chip.component';
import { LeadFormModal } from '../../shared/lead-form-modal.component';
import { ImportLeadsModal } from './components/import-leads-modal.component';
import { BulkAssignBar } from './components/bulk-assign-bar.component';

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';
const TH = 'whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500';
const TD = 'border-t border-gray-100 px-3 py-3 whitespace-nowrap';

/**
 * Vertical first, then the chips, then who holds it, then who it is.
 *
 * Ordered by what someone scanning the queue asks in sequence: which funnel,
 * what state is it in, whose is it, who is it. The name is the last of those
 * and is rarely what you search by, so it sits after the chips rather than
 * leading.
 *
 * **No phone or email column.** The table is the one place the whole book is
 * on screen at once, which makes it the one place a contact list can be
 * selected and copied wholesale. The filter still searches by phone and the
 * detail page still shows it — you just cannot lift the lot in one go.
 */
const COLUMNS = [
  '#',
  'Vertical',
  'Source',
  'Labels',
  'Assignee',
  'Name',
  'Last comment',
  'Created',
  'Updated'
];

/*
  Columns whose width is pinned rather than left to the content.

  The Source chip expands on hover. In an auto-layout table that widened its
  cell and shoved every column to the right of it, so the whole table lurched
  as the pointer moved down the queue. Reserving the expanded width on both
  the header and the cell means the hover changes what is drawn and never
  what is laid out.
*/
const COLUMN_WIDTH: Record<string, string> = {
  Source: 'w-[11rem] min-w-[11rem]'
};

interface Option {
  value: string;
  label: string;
}

export const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase());

const toOptions = (values: string[]): Option[] =>
  values.map((value: string) => ({ value, label: titleCase(value) }));

export const LeadsListContainer: React.FC = () => {
  const history = useHistory();
  const [leads, setLeads] = useState<LeadUiModel[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [config, setConfig] = useState<LeadConfigModel | undefined>();
  const [query, setQuery] = useState<LeadSearchQueryModel>({});
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  /**
   * Leads ticked for a bulk action.
   *
   * Kept as ids rather than rows so a selection survives a reload: after a
   * bulk assign the list refetches, and holding whole objects would leave the
   * bar showing stale names.
   */
  const [selected, setSelected] = useState<string[]>([]);
  const [phoneInput, setPhoneInput] = useState('');

  /**
   * The phone box is debounced; the dropdowns are not.
   *
   * A dropdown produces one deliberate change, so searching on it immediately
   * is right. Typing a phone number produces one per keystroke — eleven
   * searches for one number, of which ten are for prefixes nobody asked about,
   * and whose responses can land out of order and show the wrong result for
   * the number on screen.
   *
   * `phoneInput` updates on every keystroke so typing stays responsive; only
   * the committed value reaches the query.
   */
  useEffect(() => {
    const trimmed = phoneInput.trim();
    const current = query.phone ?? '';
    if (trimmed === current) return;

    const timer = setTimeout(() => {
      setOffset(0);
      setQuery((previous) => ({ ...previous, phone: trimmed || undefined }));
    }, 400);
    return () => clearTimeout(timer);
    // `query.phone` is read to skip a no-op, but must not re-arm the timer on
    // its own change — that is the update this effect just made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneInput]);

  useEffect(() => {
    getLeadConfig().then((result) => {
      if (!isErrorInfo(result)) setConfig(result);
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    return searchLeads(query, limit, offset).then((result) => {
      setLoadError(isErrorInfo(result) ? describeLoadError(result) : undefined);
      if (!isErrorInfo(result)) {
        setLeads(result.data);
        setTotal(result.total);
      }
      setLoading(false);
    });
  }, [query, limit, offset]);

  useEffect(() => {
    load();
  }, [load]);

  /** Changing a filter returns to page 1 — page 4 of the old result set is meaningless. */
  const setFilter = (patch: Partial<LeadSearchQueryModel>) => {
    setOffset(0);
    setQuery({ ...query, ...patch });
  };

  const verticalOptions = useMemo(() => toOptions(config?.verticals ?? []), [config]);
  const statusOptions = useMemo(() => toOptions(config?.statuses ?? []), [config]);
  const valueOptions = useMemo(() => toOptions(config?.valueCategories ?? []), [config]);

  const single = (
    key: 'vertical' | 'status' | 'valueCategory',
    label: string,
    options: Option[]
  ) => (
    <div>
      <label className={LABEL}>{label}</label>
      <Select
        isClearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={selectMenuPortalStyles}
        classNamePrefix="react-select"
        options={options}
        placeholder={`All ${label.toLowerCase()}`}
        value={options.find((option: Option) => option.value === query[key]) ?? null}
        onChange={(option: ValueType<Option, false>) =>
          setFilter({
            [key]: option ? (option as Option).value : undefined
          } as Partial<LeadSearchQueryModel>)
        }
      />
    </div>
  );

  const handleCreate = (payload: Record<string, unknown>) => {
    const promise = createLead(payload).then((response) => {
      if (isErrorInfo(response as ErrorInfo)) throw response;
      const created = response as { id: string; shortId?: number; created: boolean };
      setShowCreate(false);
      // `created: false` means the service matched an open lead for the same
      // person in the same vertical and handed that one back. Saying "created"
      // would have someone looking for a record that does not exist.
      birdToast.success(
        created.created
          ? `Lead #${created.shortId ?? ''} created`
          : `That person already has an open lead in this funnel — opened #${
              created.shortId ?? ''
            } instead`
      );
      setOffset(0);
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Creating lead...',
      successMessage: 'Done',
      errorPermissionMessage: 'You do not have permission to create leads',
      errorFailedMessage: 'Failed to create the lead'
    });
  };

  const detailsPath = (leadId: string) =>
    (APP_ROUTES.LEAD_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH.replace(':leadId', leadId);

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-wrap items-center gap-3">
        <span className="h-8 w-1.5 rounded-full bg-secondary-500" aria-hidden="true" />
        <h1 className="font-gilroy-bold text-2xl text-neutral-500">Leads</h1>
        {!loading ? (
          <span className={`${CHIP} bg-primary-50 text-primary-700`}>
            {total} lead{total === 1 ? '' : 's'}
          </span>
        ) : null}
        <span className="ml-auto flex flex-wrap gap-3">
          <Button type={ButtonType.gray} disabled={!config} onClick={() => setShowImport(true)}>
            Upload leads
          </Button>
          <Button type={ButtonType.primary} disabled={!config} onClick={() => setShowCreate(true)}>
            Create lead
          </Button>
        </span>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-md">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {single('vertical', 'Verticals', verticalOptions)}
          {single('status', 'Statuses', statusOptions)}
          {single('valueCategory', 'Values', valueOptions)}

          <div>
            {/* The team does not have user ids to hand, so this searches people. */}
            <label className={LABEL}>Assignee</label>
            <StaffPicker
              value={query.assignedTo}
              onChange={(userId?: string) => setFilter({ assignedTo: userId })}
              placeholder="Anyone"
            />
          </div>

          <div>
            <label className={LABEL}>Phone</label>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Any"
              value={phoneInput}
              onChange={(event) => setPhoneInput(event.target.value)}
            />
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-neutral-400">
              <input
                type="checkbox"
                checked={Boolean(query.slaBreachedOnly)}
                onChange={(event) =>
                  setFilter({ slaBreachedOnly: event.target.checked || undefined })
                }
              />
              Breached SLA only
            </label>
          </div>
        </div>
      </section>

      <section>
        {loading ? (
          <LoadingData />
        ) : loadError ? (
          <LoadError message={loadError} onRetry={load} />
        ) : leads.length === 0 ? (
          <NotFoundData />
        ) : (
          <>
            {selected.length ? (
              <BulkAssignBar
                selectedIds={selected}
                maxLeads={config?.maxBulkAssignLeads ?? 200}
                onClear={() => setSelected([])}
                onDone={() => {
                  setSelected([]);
                  load();
                }}
              />
            ) : null}

            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="bg-creams-200">
                    <th className={TH}>
                      {/*
                        Selects what is on screen, not every lead matching the
                        filter. A tick that silently covers eight hundred rows
                        the operator cannot see is how a bulk action goes
                        somewhere nobody intended.
                      */}
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={selected.length > 0 && selected.length === leads.length}
                        onChange={(event) =>
                          setSelected(
                            event.target.checked ? leads.map((lead: LeadUiModel) => lead.id) : []
                          )
                        }
                      />
                    </th>
                    {COLUMNS.map((column: string) => (
                      <th key={column} className={`${TH} ${COLUMN_WIDTH[column] ?? ''}`}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead: LeadUiModel) => (
                    <tr
                      key={lead.id}
                      className="cursor-pointer hover:bg-creams-100"
                      onClick={() => history.push(detailsPath(lead.id))}
                    >
                      <td className={TD} onClick={(event) => event.stopPropagation()}>
                        {/*
                          Stops the row's navigate-to-details click. Without it
                          ticking a box opens the lead, which makes selecting
                          more than one impossible.
                        */}
                        <input
                          type="checkbox"
                          aria-label={`Select lead ${lead.shortId}`}
                          checked={selected.includes(lead.id)}
                          onChange={(event) =>
                            setSelected((current: string[]) =>
                              event.target.checked
                                ? [...current, lead.id]
                                : current.filter((id: string) => id !== lead.id)
                            )
                          }
                        />
                      </td>
                      <td className={`${TD} text-neutral-300`}>{lead.shortId}</td>
                      <td className={TD}>
                        <span className={`${CHIP} ${styleFor(VERTICAL_STYLE, lead.vertical)}`}>
                          {titleCase(lead.vertical)}
                        </span>
                      </td>
                      {/*
                        How the lead arrived, next to what kind of lead it is.
                        One chip: the mechanism, plus the marketing channel
                        when we know it, because "Facebook" answers the
                        question better than "web form" — which describes
                        almost every lead.

                        Everything else lives in the tooltip. A queue is for
                        scanning; the campaign, the landing page and the click
                        ids matter when a number looks wrong, not on a call.
                      */}
                      <td className={`${TD} ${COLUMN_WIDTH.Source}`}>
                        <LeadSourceChip lead={lead} />
                      </td>
                      <td className={TD}>
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`${CHIP_PRIMARY} ${styleFor(STATUS_STYLE, lead.status)}`}
                          >
                            {titleCase(lead.status)}
                          </span>
                          {lead.resolutionType ? (
                            <span
                              className={`${CHIP_SECONDARY} ${resolutionStyle(
                                lead.resolutionType,
                                config?.wonResolutionTypes,
                                config?.systemOnlyResolutionTypes
                              )}`}
                            >
                              {titleCase(lead.resolutionType)}
                            </span>
                          ) : null}
                          {/*
                            Why it is on hold, next to the status it qualifies.
                            "Pending" alone tells an agent a lead is stalled
                            but not whether it is waiting on us or on the
                            customer, which is the thing that decides whether
                            to chase it.

                            Keyed on presence rather than on status === pending:
                            the service clears pendingReason on every
                            transition away from pending, so a value here is
                            always current, and hiding one that somehow
                            survived would mask a real bug rather than show it.
                          */}
                          {lead.pendingReason ? (
                            <span
                              className={`${CHIP_SECONDARY} ${PENDING_REASON_CHIP}`}
                              title={`On hold — ${titleCase(lead.pendingReason)}`}
                            >
                              {titleCase(lead.pendingReason)}
                            </span>
                          ) : null}
                          <SlaBadge state={lead.slaState} weight="quiet" />
                          {/*
                            "2 left", not "2 / 2". The figure is attempts
                            REMAINING, and a bare ratio reads as attempts used
                            — it was reported as a bug on a lead nobody had
                            called yet.
                          */}
                          <span
                            className={`${CHIP_QUIET} ${
                              lead.remainingContactAttempts === 0
                                ? 'text-secondary-700'
                                : 'text-neutral-400'
                            }`}
                            title={`${lead.maxContactAttempts - lead.remainingContactAttempts} of ${
                              lead.maxContactAttempts
                            } contact attempts used`}
                          >
                            {lead.remainingContactAttempts === 0
                              ? 'No attempts left'
                              : `${lead.remainingContactAttempts} left`}
                          </span>
                        </span>
                      </td>
                      <td className={TD}>
                        {lead.assigneeName ?? (
                          <span
                            className={`${CHIP} bg-creams-500 text-creams-900`}
                            title="Nobody holds this lead — it stays in the queue"
                          >
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className={`${TD} font-gilroy-medium text-neutral-500`}>
                        {lead.name || '—'}
                      </td>
                      <td className={`${TD} text-neutral-400`}>
                        {lead.lastComment ? (
                          /*
                            Truncated to the column with the full note in the
                            tooltip. `title` rather than a custom popover on
                            purpose: an agent scanning the queue wants the gist
                            without leaving the row, and a note long enough to
                            need the full text is better read on the lead.

                            `max-w-[18rem]`, NOT `max-w-0 w-[18rem]`. That
                            pairing shipped once and rendered every populated
                            cell blank: max-width wins over width, so the span
                            computed to zero and `truncate`'s overflow:hidden
                            swallowed the text. The leads with no comment still
                            showed their dash, which made it read as missing
                            data rather than as a layout bug.

                            `max-w-0` is the fixed-table-layout idiom and
                            belongs on the td, not on a span inside an
                            auto-layout cell.
                          */
                          <span className="block max-w-[18rem] truncate" title={lead.lastComment}>
                            {lead.lastComment}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={`${TD} text-neutral-400`}>
                        {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className={`${TD} text-neutral-400`}>
                        {lead.updatedAt ? new Date(lead.updatedAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > 0 ? (
              <div className="flex items-center justify-center py-4">
                <Pagination
                  itemCounts={total}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={(newOffset: number) => setOffset(newOffset)}
                  handleItemsPerPage={(itemsPerPage: number) => {
                    setOffset(0);
                    setLimit(itemsPerPage);
                  }}
                />
              </div>
            ) : null}
          </>
        )}
      </section>

      {config ? (
        <ImportLeadsModal
          show={showImport}
          config={config}
          onClose={() => setShowImport(false)}
          onImported={load}
        />
      ) : null}

      {config ? (
        <LeadFormModal
          show={showCreate}
          config={config}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      ) : null}
    </div>
  );
};
